import { generatePath } from 'react-router-dom';
import type { ConversationListItem } from '@/entities/conversations';
import { apiClient } from '@/shared/api';
import type { SearchResult } from '../types';
import { search } from '../api';
import type {
  SearchConversationDeleteRequest,
  SearchConversationDeleteResult,
  SearchConversationDownloadRequest,
  SearchConversationRequest,
  SearchConversationRunPipelineRequest,
  SearchConversationRunPipelineResponse,
  TranscriptArchiveRequest,
  TranscriptArchiveStartResponse,
  TranscriptArchiveStatusResponse,
} from './types';

export const searchConversations = async (
  request: Omit<SearchConversationRequest, 'entityType'>,
): Promise<SearchResult<ConversationListItem>> => {
  return search<ConversationListItem>({
    ...request,
    entityType: 'conversation',
  });
};

/**
 * Выгрузка найденных диалогов файлом через POST /search/query/download.
 * Тело — та же выборка, что и у поиска, плюс вид таблицы на экране; постраничности нет.
 * Файл приходит один, `.xlsx`: формат не выбирается.
 *
 * `silentError` подавляет общее всплывающее уведомление: причина отказа приходит телом ответа,
 * которое читается файлом, и общее уведомление показало бы вместо неё безымянную ошибку.
 * О неудаче сообщает сам вызывающий, разобрав тело ответа.
 */
export const downloadConversationsSearch = async (
  request: Omit<SearchConversationDownloadRequest, 'entityType'>,
): Promise<Blob> => {
  const { data } = await apiClient.post<Blob>(
    '/search/query/download',
    { ...request, entityType: 'conversation' },
    { responseType: 'blob', silentError: true },
  );
  return data;
};

/**
 * Запуск фонового формирования архива транскрипций через POST /search/query/transcript-archive.
 *
 * `silentError` у запросов архива: неудачу на любом шаге — запуск, опрос, файл — сообщает одним
 * уведомлением вызывающий, иначе рядом с ним всплывало бы общее, а у файла ещё и безымянное.
 */
export const startTranscriptArchive = async (
  request: Omit<TranscriptArchiveRequest, 'entityType'>,
): Promise<TranscriptArchiveStartResponse> => {
  const { data } = await apiClient.post<TranscriptArchiveStartResponse>(
    '/search/query/transcript-archive',
    { ...request, entityType: 'conversation' },
    { silentError: true },
  );
  return data;
};

/** Состояние формирования архива транскрипций (GET /search/query/transcript-archive/{archiveId}) */
export const fetchTranscriptArchiveStatus = async (archiveId: string): Promise<TranscriptArchiveStatusResponse> => {
  const { data } = await apiClient.get<TranscriptArchiveStatusResponse>(
    generatePath('/search/query/transcript-archive/:archiveId', { archiveId }),
    { silentError: true },
  );
  return data;
};

/** Готовый zip-архив транскрипций (GET /search/query/transcript-archive/{archiveId}/file) */
export const downloadTranscriptArchive = async (archiveId: string): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(
    generatePath('/search/query/transcript-archive/:archiveId/file', { archiveId }),
    { responseType: 'blob', silentError: true },
  );
  return data;
};

/**
 * Массовый запуск обработки диалогов через POST /search/query/run-pipeline.
 * Тело — та же выборка, что и у поиска, плюс выбранный пайплайн; постраничности нет.
 */
export const runConversationsPipeline = async (
  request: Omit<SearchConversationRunPipelineRequest, 'entityType'>,
): Promise<SearchConversationRunPipelineResponse> => {
  const { data } = await apiClient.post<SearchConversationRunPipelineResponse>('/search/query/run-pipeline', {
    ...request,
    entityType: 'conversation',
  });
  return data;
};

/**
 * Удаление выбранных диалогов через POST /search/query/delete.
 * Удаляются только диалоги из `filters.conversationIds`, доступные пользователю; все разом или ни один.
 *
 * `silentError` подавляет общее всплывающее уведомление: об исходе удаления — и об успехе,
 * и об отказе — сообщает вызывающий, называя действие, а причину отказа берёт из тела ответа.
 */
export const deleteConversations = async (
  request: Omit<SearchConversationDeleteRequest, 'entityType'>,
): Promise<SearchConversationDeleteResult> => {
  const { data } = await apiClient.post<SearchConversationDeleteResult>(
    '/search/query/delete',
    { ...request, entityType: 'conversation' },
    { silentError: true },
  );
  return data;
};
